import {
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  useTheme,
  Typography,
  Avatar,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Skeleton,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Search, Close, Delete, History, PlayArrow, Dashboard as DashboardIcon } from '@mui/icons-material';
import { useCreateAMCMutation, useEnableAMCMutation, useDisableAMCMutation, useGetAMCsQuery } from '../services/amcApi';
import { AMCForm, AMCFormValues } from '@/components/AMCForm';
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';

function TableHeadCell({ children }: { children?: React.ReactNode }) {
  return (
    <TableCell
      sx={{
        p: 2,
      }}
    >
      {children}
    </TableCell>
  );
}

const AMCList = () => {
  const [createAMCOpen, setCreateAMCOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data, isLoading } = useGetAMCsQuery();
  // Get user info from Redux store
  const user = useSelector((state: RootState) => state.user);
  const isAdmin = user.role === 'admin';

  const allAmcs = data?.map((amc) => ({ ...amc })) || [];
  // Filter AMCs based on status
  const amcs = allAmcs.filter((amc) => {
    if (statusFilter === 'active') return amc.isActive;
    if (statusFilter === 'inactive') return !amc.isActive;
    return true; // 'all' shows everything
  });

  const [createAMC] = useCreateAMCMutation();
  const [deleteAMC] = useDisableAMCMutation();
  const [enableAMC] = useEnableAMCMutation();
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [chosenAMCId, setChosenAMCId] = useState<string | null>(null);
  const [actionType, setActionType] = useState<'disable' | 'enable'>('disable');

  const showAMCDeleteDialog = (amcId: string, action: 'disable' | 'enable') => {
    setChosenAMCId(amcId);
    setActionDialogOpen(true);
    setActionType(action);
  };
  const handleDeleteAMC = async () => {
    if (chosenAMCId) {
      try {
        if (actionType === 'disable') {
          await deleteAMC(chosenAMCId).unwrap();
          enqueueSnackbar('AMC Disabled successfully', { variant: 'success' });
        }
        else {
          await enableAMC(chosenAMCId).unwrap();
          enqueueSnackbar('AMC Enabled successfully', { variant: 'success' });
        }
        setChosenAMCId(null);
        setActionDialogOpen(false);
      } catch (error: any) {
        enqueueSnackbar(error.data?.message || 'Failed to Disable AMC', { variant: 'error' });
      }
    }
  };
  const handleCreateAMCClose = () => {
    setCreateAMCOpen(false);
  };
  const handleSubmit = async (values: AMCFormValues) => {
    await createAMC(values).unwrap();
    handleCreateAMCClose();
  };

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDashboardClick = (amcId: string) => {
    navigate(`/dashboard?amcId=${amcId}`);
  };

  if (isLoading) {
    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Skeleton variant="text" width={200} height={40} />
          <Skeleton variant="rectangular" width={120} height={40} />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width={300} height={56} />
        </Box>

        <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: '16px' }}>
          <Table>
            <TableHead>
              <TableRow>
                {[...Array(isAdmin ? 6 : 5)].map((_, i) => (
                  <TableCell key={i}>
                    <Skeleton variant="text" />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[...Array(5)].map((_, index) => (
                <TableRow key={index}>
                  {[...Array(isAdmin ? 6 : 5)].map((_, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Skeleton variant="text" width={250} height={40} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Breadcrumbs
        separator="·"
        sx={{
          mb: 1,
          '& .MuiBreadcrumbs-separator': {
            mx: 1, // Add some margin around the dot
            color: theme.palette.text.secondary, // Style the dot color
          },
        }}
      >
        <Link
          component={RouterLink}
          to="/dashboard"
          sx={{
            color: theme.palette.primary.main,
            fontSize: theme.typography.subtitle1.fontSize,
            fontWeight: theme.typography.subtitle1.fontWeight,
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          Dashboard
        </Link>
        <Typography variant="subtitle1">Companies</Typography>
      </Breadcrumbs>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <Typography sx={{ fontWeight: 'bold', fontSize: '24px', mb: isMobile ? 2 : 0 }}>
          Companies
        </Typography>
        <Button
          size="small"
          color="primary"
          variant="contained"
          disableElevation
          sx={{
            fontSize: '12px',
            borderRadius: '7px',
            width: isMobile ? '100%' : 'auto',
          }}
          startIcon={<AddIcon />}
          onClick={() => setCreateAMCOpen(true)}
        >
          Create Company
        </Button>
      </Box>
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: '8px',
          display: 'flex',
          gap: 2,
          p: 2,
          alignItems: 'center',
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <TextField
          size="small"
          variant="outlined"
          sx={{ width: isMobile ? '100%' : 300 }}
          label="Status"
          select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
        >
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
          <MenuItem value="all">All</MenuItem>
        </TextField>
        <TextField
          InputProps={{ startAdornment: <Search color="inherit" /> }}
          fullWidth
          label="Seach"
          size="small"
        />
      </Box>
      {isMobile ? (
        <Box sx={{ mt: 2 }}>
          {amcs.map((amc) => (
            <Paper key={amc.id} sx={{ p: 2, mb: 2, opacity: amc.isActive ? 1 : 0.5, backgroundColor: amc.isActive ? 'white' : 'rgba(255, 255, 255, 0.5)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Link
                  component={RouterLink}
                  to={`/amcs/${amc.id}/assets`}
                  sx={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        borderRadius: '7px',
                        border: theme.borders.borderLight,
                        width: '34px',
                        height: '34px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      {amc.logoPath ? (
                        <img
                          src={amc.logoPath}
                          alt="AMC Logo"
                          style={{ width: '27px', height: '27px' }}
                        />
                      ) : (
                        <Avatar sx={{ borderRadius: '4px', width: '27px', height: '27px' }}>
                          {amc.name.charAt(0).toUpperCase()}
                        </Avatar>
                      )}
                    </Box>
                    <Typography variant="h6">{amc.name}</Typography>
                  </Box>
                </Link>
                <IconButton
                  size="medium"
                  component={RouterLink}
                  to={`/amcs/${amc.id}/history`}
                >
                  <History fontSize="small" />
                </IconButton>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  {isAdmin && (
                    <IconButton
                      size="medium"
                      onClick={() => handleDashboardClick(amc.id)}
                      sx={{
                        color: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.light + '20',
                        }
                      }}
                    >
                      <DashboardIcon fontSize="small" />
                    </IconButton>
                  )}
                  {amc.isActive ? (
                    <IconButton size="medium" onClick={() => showAMCDeleteDialog(amc.id, 'disable')}>
                      <Delete fontSize="small" color="error" />
                    </IconButton>
                  ) : (
                    <IconButton size="medium" onClick={() => showAMCDeleteDialog(amc.id, 'enable')}>
                      <PlayArrow fontSize="small" color="error" />
                    </IconButton>
                  )}
                </Box>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">ID: {amc.fundCode}</Typography>
                <Typography variant="body2">
                  <Link
                    component={RouterLink}
                    to={`/amcs/${amc.id}/merchant-configs`}
                    sx={{ textDecoration: 'none' }}
                  >
                    {amc.merchantConfigs?.length} Aggregator
                    {amc.merchantConfigs?.length === 1 ? '' : 's'} Configured
                  </Link>
                </Typography>
                <Typography variant="body2">
                  <Link
                    component={RouterLink}
                    to={`/amcs/${amc.id}/assets`}
                    sx={{ textDecoration: 'none' }}
                  >
                    {amc.assets?.length} Assets
                  </Link>
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: '16px', mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ fontWeight: 'bold' }}>
                <TableHeadCell>Company Name</TableHeadCell>
                <TableHeadCell>ID</TableHeadCell>
                <TableHeadCell>Payment Aggregators</TableHeadCell>
                <TableHeadCell>Assets</TableHeadCell>
                {isAdmin && <TableHeadCell>Dashboard</TableHeadCell>}
                <TableHeadCell></TableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {amcs.map((amc) => (
                <TableRow key={amc.id} hover sx={{ opacity: amc.isActive ? 1 : 0.5, backgroundColor: amc.isActive ? 'white' : 'rgba(255, 255, 255, 0.5)' }}>
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={`/amcs/${amc.id}/assets`}
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: theme.typography.subtitle1.fontSize,
                        fontWeight: theme.typography.subtitle1.fontWeight,
                        textDecoration: 'none',
                      }}
                    >
                      <Box
                        sx={{
                          height: '100%',
                          display: 'flex',
                          mt: 0.7,
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            borderRadius: '7px',
                            border: theme.borders.borderLight,
                            width: '34px',
                            height: '34px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          {amc.logoPath ? (
                            <img
                              src={amc.logoPath}
                              alt="AMC Logo"
                              style={{ width: '27px', height: '27px' }}
                            />
                          ) : (
                            <Avatar
                              sx={{
                                borderRadius: '4px',
                                width: '27px',
                                height: '27px',
                              }}
                            >
                              {amc.name.charAt(0).toUpperCase()}
                            </Avatar>
                          )}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: theme.typography.subtitle1.fontSize,
                            color: theme.palette.primary.main,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          }}
                        >
                          {amc.name}
                        </Typography>
                      </Box>
                    </Link>
                  </TableCell>
                  <TableCell>{amc.fundCode}</TableCell>
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={`/amcs/${amc.id}/merchant-configs`}
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: theme.typography.subtitle1.fontSize,
                        fontWeight: theme.typography.subtitle1.fontWeight,
                        textDecoration: 'none',
                      }}
                    >
                      <Box
                        sx={{
                          '&:hover': {
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {amc.merchantConfigs?.length} Aggregator
                        {amc.merchantConfigs?.length === 1 ? '' : 's'} Configured
                      </Box>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={`/amcs/${amc.id}/assets`}
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: theme.typography.subtitle1.fontSize,
                        fontWeight: theme.typography.subtitle1.fontWeight,
                        textDecoration: 'none',
                      }}
                    >
                      <Box
                        sx={{
                          '&:hover': {
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {amc.assets?.length} Assets
                      </Box>
                    </Link>
                  </TableCell>
                  {isAdmin && (
                    <TableCell>
                      <Typography
                        variant="body2"
                        onClick={() => handleDashboardClick(amc.id)}
                        sx={{
                          color: theme.palette.primary.main,
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          fontSize: '14px',
                        }}
                      >
                        Dashboard
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <Tooltip title="History">
                      <IconButton size="medium" component={RouterLink} to={`/amcs/${amc.id}/history`}>
                        <History fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {amc.isActive ? (
                      <Tooltip title="Disable">
                        <IconButton size="medium" onClick={() => showAMCDeleteDialog(amc.id, 'disable')}>
                          <Delete fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Enable">
                        <IconButton size="medium" onClick={() => showAMCDeleteDialog(amc.id, 'enable')}>
                          <PlayArrow fontSize="small" color="success" />
                        </IconButton>
                      </Tooltip>
                    )
                    }
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={amcs.length}
        rowsPerPage={pageSize}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
      />

      <Dialog open={createAMCOpen} onClose={handleCreateAMCClose} fullWidth>
        <DialogTitle>
          <Typography sx={{ fontWeight: 'bold', fontSize: '23px' }}>Create Company</Typography>
          <IconButton
            aria-label="close"
            onClick={handleCreateAMCClose}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div style={{ margin: '0.5em' }} />
          <AMCForm
            initialValues={{
              fundCode: '',
              name: '',
              useExternalUpiWebhook: false,
              isRTA: false,
              isActive: true,
            }}
            onSubmit={handleSubmit}
            submitButtonText="Create AMC"
          />
        </DialogContent>
      </Dialog>
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)}>
        <DialogTitle>
          <Typography sx={{ fontWeight: 'bold', fontSize: '23px' }}> {actionType === 'disable' ? 'Disable AMC' : 'Enable AMC'}</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>{actionType === 'disable' ? 'This will disable the AMC and prevent it from being used in any transactions.' : 'This will enable the AMC and allow it to be used in transactions.'}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => handleDeleteAMC()} color="error">
            {actionType === 'disable' ? 'Disable' : 'Enable'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AMCList;
